import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { mapDateToTab, mapDrawToDateTab, mapMatchToTab } from '../../../matches-calendar/utils/mapping';
import { sortByTabDate } from '../../../matches-calendar/utils/sorting';
import { fetchMatchDates } from '../../api/dates';
import { fetchMatches } from '../../api/matches';

import { fetchDraws } from '@apis/draws';
import type { RoundPhase } from '@shared-types/match.types';
import { groupBy, uniqBy } from '@utilities/collection-utils';
import { dateToString } from '@utilities/dateTime';
import { Warn } from '@utilities/logging';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type LoadTabsParams = {
  competitionIds: string[];
  phase?: RoundPhase | 'ALL';
};

const fetchDates = async (params: LoadTabsParams & { startDate?: Dayjs; endDate?: Dayjs }, signal: AbortSignal) => {
  const isMultiCompetition = params.competitionIds?.length > 1;
  const dates = isMultiCompetition
    ? await fetchMatchDates(
        {
          competitionIds: params.competitionIds,
          startDate: params.startDate,
          endDate: params.endDate,
        },
        signal,
      )
    : [];
  const matches = isMultiCompetition
    ? []
    : await fetchMatches(
        {
          competitionIds: params.competitionIds,
          phase: params.phase,
          fromDate: params.startDate,
          toDate: params.endDate,
        },
        signal,
      );

  const seasonYear = matches?.at(0)?.seasonYear ?? 'current';
  const draws = isMultiCompetition
    ? []
    : (
        await fetchDraws(
          {
            competitionId: params.competitionIds?.at(0),
            seasonYear,
            optionalFields: ['ROUNDS', 'CITY'],
          },
          signal,
        )
      )
        .filter((d) => !params.phase || params.phase === 'ALL' || d.rounds.some((r) => r.round?.phase === params.phase))
        .filter(
          (d) =>
            dayjs(d.date ?? d.day).isSameOrAfter(params.startDate) &&
            dayjs(d.date ?? d.day).isSameOrBefore(params.endDate),
        );

  const matchDates = Object.entries(
    groupBy(matches, (m) => dateToString(m.kickOffTime.dateTime ?? m.kickOffTime.date, 'YYYY-MM-DD')),
  ).map(([_, matches]) => mapMatchToTab(matches.at(0), matches.length));

  const result = uniqBy(
    [...draws.map(mapDrawToDateTab), ...dates.map(mapDateToTab), ...matchDates].filter((t) => t.date),
    'id',
  ).toSorted(sortByTabDate);

  return result;
};

export const loadTabsByDate = async (
  params: LoadTabsParams & { startDate?: Dayjs; endDate?: Dayjs; dir?: 'prev' | 'next' },
  signal: AbortSignal,
) => {
  try {
    let startDate = params.startDate;
    let endDate = params.endDate;

    const result = await fetchDates({ ...params, startDate, endDate }, signal);

    if (result.length >= 10) {
      return result;
    }

    let iteration = 0;
    do {
      iteration++;
      if (params.dir !== 'prev') {
        endDate = endDate.add(1, 'month');
      }
      if (params.dir !== 'next') {
        startDate = startDate.subtract(1, 'month');
      }

      const newResult = await fetchDates({ ...params, startDate, endDate }, signal);
      result.push(...newResult);
    } while (result.length < 10 && iteration < 100);

    return result.toSorted(sortByTabDate);
  } catch (e) {
    Warn(e);
    throw e;
  }
};
